import React, { useState, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2 } from 'lucide-react';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const rangeBackground = (percent) =>
  `linear-gradient(to right, #c0c0c0 0%, #c0c0c0 ${percent}%, rgba(192, 192, 192, 0.2) ${percent}%, rgba(192, 192, 192, 0.2) 100%)`;

const getPlayLabel = (currentTrack, isPlaying) => {
  if (currentTrack?.spotifyUrl) return 'Play on Spotify';
  if (isPlaying) return 'Pause';
  return 'Play';
};

const TrackInfo = ({ currentTrack }) => {
  if (!currentTrack) {
    return (
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-lg bg-silver-900/30 flex items-center justify-center">
          <Play size={24} className="text-silver-500" />
        </div>
        <div>
          <p className="text-white/60 text-sm">No track selected</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-silver-900/30 flex-shrink-0">
        <img src={currentTrack.artwork} alt={currentTrack.title} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm truncate">{currentTrack.title}</p>
        <p className="text-white/60 text-xs truncate">{currentTrack.artist}</p>
      </div>
    </>
  );
};

const PlaybackControls = ({ currentTrack, isPlaying, onTogglePlay }) => (
  <div className="flex items-center gap-4">
    <button
      type="button"
      className="text-white/60 hover:text-silver-400 transition-colors disabled:opacity-30"
      disabled={!currentTrack}
      aria-label="Previous track"
    >
      <SkipBack size={20} />
    </button>

    <button
      type="button"
      onClick={onTogglePlay}
      disabled={!currentTrack}
      className="w-10 h-10 rounded-full bg-silver-400 hover:bg-silver-300 flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:hover:bg-silver-400"
      aria-label={getPlayLabel(currentTrack, isPlaying)}
      title={currentTrack?.spotifyUrl ? 'Click to play on Spotify' : ''}
    >
      {isPlaying ? <Pause size={20} className="text-black" /> : <Play size={20} className="text-black ml-0.5" />}
    </button>

    <button
      type="button"
      className="text-white/60 hover:text-silver-400 transition-colors disabled:opacity-30"
      disabled={!currentTrack}
      aria-label="Next track"
    >
      <SkipForward size={20} />
    </button>
  </div>
);

const ProgressBar = ({ currentTrack, currentTime, duration, onSeek }) => (
  <div className="flex items-center gap-3 w-full max-w-lg">
    <span className="text-white/60 text-xs min-w-[40px]">{formatTime(currentTime)}</span>
    <input
      type="range"
      min="0"
      max={duration}
      value={currentTime}
      onChange={(e) => onSeek(parseInt(e.target.value))}
      disabled={!currentTrack}
      className="flex-1 h-1 bg-silver-900/50 rounded-full appearance-none cursor-pointer disabled:opacity-30"
      style={{
        background: currentTrack ? rangeBackground((currentTime / duration) * 100) : 'rgba(192, 192, 192, 0.2)'
      }}
    />
    <span className="text-white/60 text-xs min-w-[40px]">{formatTime(duration)}</span>
  </div>
);

const VolumeControl = ({ volume, onChange }) => (
  <div className="hidden md:flex items-center gap-3 w-1/4 justify-end">
    <Volume2 size={20} className="text-white/60" />
    <input
      type="range"
      min="0"
      max="100"
      value={volume}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-24 h-1 bg-silver-900/50 rounded-full appearance-none cursor-pointer"
      style={{ background: rangeBackground(volume) }}
    />
  </div>
);

const MusicPlayer = ({ currentTrack }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(240); // Mock 4 minute duration
  const [volume, setVolume] = useState(70);

  useEffect(() => {
    if (!isPlaying) return undefined;
    const interval = setInterval(() => {
      setCurrentTime((prev) => {
        if (prev >= duration) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying, duration]);

  const togglePlayPause = () => {
    if (currentTrack?.spotifyUrl) {
      window.open(currentTrack.spotifyUrl, '_blank');
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-silver-900/30 z-40">
      <div className="container mx-auto px-6 py-4">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="flex items-center gap-4 w-full md:w-1/4">
            <TrackInfo currentTrack={currentTrack} />
          </div>

          <div className="flex-1 w-full md:w-auto">
            <div className="flex flex-col items-center gap-2">
              <PlaybackControls currentTrack={currentTrack} isPlaying={isPlaying} onTogglePlay={togglePlayPause} />
              <ProgressBar
                currentTrack={currentTrack}
                currentTime={currentTime}
                duration={duration}
                onSeek={setCurrentTime}
              />
            </div>
          </div>

          <VolumeControl volume={volume} onChange={setVolume} />
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
